import React from "react";
import SubjectProfile from "./SubjectProfile";
import AssociatedPartyProfile from "./AssociatedPartyProfile";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import useInvestorAmlHistoryStore from "../zstore/investorAmlHistory";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import { useSelector } from "react-redux";
import { selectFromAmlStore } from "../amlSlice";
import IdVerificationProfile from "./IdVerificationProfile";
import PepAndSanctionsProfile from "./PepAndSanctionsProfile";
import RiskRatingProfile from "./RiskRatingProfile";
import useInvestorRiskRatingStore from "../zstore/investorRiskRating";
import RequiredEvidenceProfile from "./RequiredEvidenceProfile";
import ReviewDocumentsProfile from "./ReviewDocumentsProfile";
import RecordOutcomeProfile from "./RecordOutcomeProfile";
import NotificationProfile from "./NotificationProfile";
import useHighlightStore from "../zstore/highlightStore";
import useInvestorEvidenceRequestStore from "../zstore/investorEvidenceRequest";
import useRequiredEvidenceTypesStore from "../zstore/requiredEvidenceTypes";
import useInvestorAmlDocumentStore from "../zstore/investorAmlDocument";
import useNotificationHistoryStore from "../zstore/investorNotificationHistory";
import useAssociatedPartyStore from "../zstore/associatedPartyStore";
import OrganisationPartyProfile from "./OrganisationPartyProfile";
import NonIndividualSummaryProfile from "./NonIndividualSummaryProfile";

type ProfileGroupProps = {
  investorId: string;
};

type DataFetchSelector = <T>(state: { fetchData: T }) => T;
const dataFetchSelector: DataFetchSelector = (state) => state.fetchData;

const markersSelector = selectFromAmlStore((state) => state.markers);

const ProfileGroup: React.FC<ProfileGroupProps> = ({ investorId }) => {
  const fetchInvestorAmlDetail = useInvestorAmlDetailStore(dataFetchSelector);
  const fetchAmlHistory = useInvestorAmlHistoryStore(dataFetchSelector);
  const fetchInvestorMarkers = useInvestorMarkerStore(dataFetchSelector);
  const fetchInvestorRiskRating = useInvestorRiskRatingStore(dataFetchSelector);
  const fetchInvestorEvidenceRequest =
    useInvestorEvidenceRequestStore(dataFetchSelector);
  const fetchInvestorAmlDocuments =
    useInvestorAmlDocumentStore(dataFetchSelector);
  const fetchNotificationHistory =
    useNotificationHistoryStore(dataFetchSelector);
  const markers = useSelector(markersSelector);

  // use React Query-based hook, scoped to this investor
  const {
    data: requiredEvidenceTypes,
    loading: requiredEvidenceLoading,
    error: requiredEvidenceError,
  } = useRequiredEvidenceTypesStore(investorId);

  const clearHightItems = useHighlightStore((state) => state.clear);
  const fetchAssociatedParties = useAssociatedPartyStore(dataFetchSelector);

  React.useEffect(() => {
    if (!investorId) return;

    // (re)load data whenever investorId changes
    clearHightItems();
    // wait until required evidence types have been loaded (or erred) before using them
    if (requiredEvidenceLoading) return;
    if (requiredEvidenceError) {
      // optional: log or handle error
      console.error(
        "Failed to load required evidence types",
        requiredEvidenceError
      );
    }

    fetchInvestorAmlDetail(investorId);
    fetchAmlHistory(investorId);
    fetchInvestorMarkers(investorId, markers);
    fetchInvestorRiskRating(investorId);
    fetchInvestorEvidenceRequest(investorId, requiredEvidenceTypes || []);
    fetchInvestorAmlDocuments(investorId);
    fetchNotificationHistory(investorId);
    clearHightItems();
    fetchAssociatedParties(investorId);

    // markers included because they're used in fetchInvestorMarkers
  }, [
    investorId,
    markers,
    requiredEvidenceTypes,
    requiredEvidenceLoading,
    requiredEvidenceError,
    fetchInvestorAmlDetail,
    fetchAmlHistory,
    fetchInvestorMarkers,
    fetchInvestorRiskRating,
    fetchInvestorEvidenceRequest,
    fetchInvestorAmlDocuments,
    fetchNotificationHistory,
    clearHightItems,
    fetchAssociatedParties,
  ]);

  return (
    <>
      <SubjectProfile personId={investorId} />
      <NonIndividualSummaryProfile personId={investorId} markers={markers} />
      <OrganisationPartyProfile personId={investorId} markers={markers} />
      <AssociatedPartyProfile personId={investorId} markers={markers} />
      <IdVerificationProfile personId={investorId} />
      <PepAndSanctionsProfile personId={investorId} />
      <RiskRatingProfile personId={investorId} />
      <RequiredEvidenceProfile personId={investorId} />
      <NotificationProfile personId={investorId} />
      <ReviewDocumentsProfile personId={investorId} />
      <RecordOutcomeProfile personId={investorId} />
    </>
  );
};

export default ProfileGroup;
